import Vue from "vue";
import axios from "@/plugins/axios";
import { PDF_ONLY_ERROR, isPdfFile } from "@/utils/submissionDocumentUpload";
import { readableReason } from "@/helpers/humanText";
import { scanUploadFailureMessage } from "@/services/scanReportMessages";
import { NOTHING_TO_OVERRIDE } from "@/services/coverageComparisonService";
import {
  QUOTE_DOCUMENT_UPLOAD_PATH,
  SUBMISSION_DETAIL_ACTION,
  resolveCitationLocally,
} from "@/services/comparisonAdapter";

/*
  Quote coverage comparison for the compare screen, shared by BWB and CWB.

  Contract (broker auth; analysis itself runs in S2R):
    - GET    /brokers/customers/:org/submissions/:id/comparison           matrix payload
    - GET    /brokers/customers/:org/submissions/:id/comparison/advisory  advisory blocks
    - POST   /brokers/customers/:org/submissions/:id/comparison/refresh   { quoteIds? } -> { enqueued }
    - POST   /brokers/customers/:org/submissions/:id<QUOTE_DOCUMENT_UPLOAD_PATH> multipart quote PDF + analyse
    - PUT    /brokers/customers/:org/submissions/:id/comparison/overrides { quoteId, rowKey, valueText, note }
    - DELETE /brokers/customers/:org/submissions/:id/comparison/overrides { quoteId, rowKey }
    - GET    /brokers/customers/:org/submissions/:id/comparison/documents?quoteId&documentKey
  Realtime: `coverageAnalysisUpdated` ({ submissionId, organizationId, quoteId, status }) and
  `comparisonUpdated` ({ submissionId, cells?, quotes? }) on the org channels.

  Both caches are keyed by submission id. A failed write returns `{ success: false, message }`
  with one plain sentence; the server's own text never reaches the broker.
*/

const IN_FLIGHT = ["queued", "running"];
const QUOTE_DOCUMENT_TYPE = "quote";
// S2BWB-344: a carrier's security scan report, read for the Security scan view.
export const SCAN_REPORT_DOCUMENT_TYPE = "scan_report";
const QUOTE_DOCUMENT_NO_QUOTE_ERROR = "Choose which quote this PDF belongs to.";
const SCAN_REPORT_NO_CARRIER_ERROR = "Choose the carrier this scan report is from.";

const UPLOAD_NOT_COMPLETED = "The document could not be uploaded. Try again.";
const REANALYSIS_FAILED = "Re-analysis could not be requested. Please try again.";
const REANALYSIS_NO_ACCESS = "You do not have access to re-analyse this submission.";
const REANALYSIS_NOT_FOUND = "This quote is no longer on the submission. Reload the page and try again.";
const REANALYSIS_NO_CONNECTION = "Could not reach the server. Check your connection and try again.";

/** One plain sentence for a refused re-analysis, chosen by status; never the server's own text. */
const reanalysisFailureMessage = (e) => {
  if (e && !e.response) return REANALYSIS_NO_CONNECTION;
  const status = e && e.response && e.response.status;
  if (status === 403) return REANALYSIS_NO_ACCESS;
  if (status === 404) return REANALYSIS_NOT_FOUND;
  return REANALYSIS_FAILED;
};

// A minute, plus a second for every 100 KB: a large PDF on a slow link is not cut off
// mid-send, while a connection that has stalled still gives up.
const uploadTimeoutMs = (file) => 60000 + Math.ceil(((file && file.size) || 0) / 100000) * 1000;

const serverUploadError = (data) => readableReason(data && (data.message || data.error), UPLOAD_NOT_COMPLETED);

/** Keep prior cells while a quote re-run is in flight (the server may return none for it). */
export function preserveCellsWhileAnalysisRuns(previous, nextPayload) {
  if (!nextPayload || typeof nextPayload !== "object") return nextPayload;
  const prevCells = previous && Array.isArray(previous.cells) ? previous.cells : [];
  if (!prevCells.length) return nextPayload;

  const refreshingQuoteIds = new Set(
    (nextPayload.quotes || [])
      .filter((q) => q && q.quoteId && IN_FLIGHT.includes(String(q.analysisStatus || "").toLowerCase()))
      .map((q) => q.quoteId)
  );
  if (!refreshingQuoteIds.size) return nextPayload;

  const nextCells = Array.isArray(nextPayload.cells) ? [...nextPayload.cells] : [];
  const present = new Set(nextCells.filter((c) => c.quoteId && c.rowKey).map((c) => `${c.quoteId}::${c.rowKey}`));
  for (const cell of prevCells) {
    if (!cell.quoteId || !cell.rowKey || !refreshingQuoteIds.has(cell.quoteId)) continue;
    const key = `${cell.quoteId}::${cell.rowKey}`;
    if (present.has(key)) continue;
    nextCells.push(cell);
    present.add(key);
  }
  return { ...nextPayload, cells: nextCells };
}

const OVERRIDE_SAVE_FAILED = "The override could not be saved. Please try again.";
const OVERRIDE_NOT_OWNER = "You can only edit or clear your own overrides.";

/**
 * One plain sentence for a refused override write, chosen by status and the API's
 * code. The server's own text, field names and codes never reach the broker.
 */
const overrideFailureMessage = (e) => {
  const status = e && e.response && e.response.status;
  const code = String((e && e.response && e.response.data && e.response.data.code) || "").toLowerCase();
  if (code === "not_found" || code === "quote_not_found" || status === 404) return NOTHING_TO_OVERRIDE;
  if (code === "not_owner" || status === 403) return OVERRIDE_NOT_OWNER;
  return OVERRIDE_SAVE_FAILED;
};

// The highlighted PDF is downloaded and annotated before S2R answers.
export const CITATION_PDF_TIMEOUT_MS = 30000;
const PDF_TYPE = "application/pdf";

const comparisonUrl = (orgId, submissionId, suffix = "") =>
  `/brokers/customers/${orgId}/submissions/${submissionId}/comparison${suffix}`;

const cellKey = (cell) => {
  const quoteId = cell.quoteId || cell.quote_id;
  const rowKey = cell.rowKey || cell.row_key;
  return quoteId && rowKey ? `${quoteId}::${rowKey}` : "";
};

export default {
  namespaced: true,

  state: () => ({
    comparisonCache: {},
    advisoryCache: {},
    // submissionId -> server message when the comparison could not be loaded
    // (feature off, customer not linked, S2R unavailable). Never a fabricated payload.
    comparisonErrors: {},
  }),

  mutations: {
    cacheComparison(state, { submissionId, payload }) {
      if (!submissionId || !payload) return;
      Vue.set(
        state.comparisonCache,
        submissionId,
        preserveCellsWhileAnalysisRuns(state.comparisonCache[submissionId], payload)
      );
      Vue.delete(state.comparisonErrors, submissionId);
    },
    setComparisonError(state, { submissionId, message }) {
      if (!submissionId) return;
      Vue.set(state.comparisonErrors, submissionId, message || "Coverage comparison is unavailable.");
    },
    cacheAdvisory(state, { submissionId, payload }) {
      if (!submissionId || !payload) return;
      Vue.set(state.advisoryCache, submissionId, payload);
    },
    dropAdvisory(state, { submissionId }) {
      if (submissionId && state.advisoryCache[submissionId]) Vue.delete(state.advisoryCache, submissionId);
    },
    patchComparisonCells(state, { submissionId, cells }) {
      const existing = state.comparisonCache[submissionId];
      if (!submissionId || !existing || !Array.isArray(cells)) return;
      // Walk the cells already there in order: a patched one is merged where it sits, and a
      // cell that carries no quote or row (nothing can address it) is kept as it is.
      const patches = {};
      for (const cell of cells) {
        const key = cellKey(cell);
        if (key) patches[key] = { ...(patches[key] || {}), ...cell };
      }
      const seen = new Set();
      const next = (existing.cells || []).map((cell) => {
        const key = cellKey(cell);
        if (!key || !patches[key]) return cell;
        seen.add(key);
        return { ...cell, ...patches[key] };
      });
      for (const key of Object.keys(patches)) {
        if (!seen.has(key)) next.push(patches[key]);
      }
      Vue.set(state.comparisonCache, submissionId, { ...existing, cells: next });
    },
    patchComparisonQuoteStatus(
      state,
      { submissionId, quoteId, analysisStatus, documentRefs, analysisProgress, hasDocument }
    ) {
      const existing = state.comparisonCache[submissionId];
      if (!submissionId || !quoteId || !existing) return;
      const quotes = (existing.quotes || []).map((q) => {
        if ((q.quoteId || q.quote_id) !== quoteId) return q;
        const next = {
          ...q,
          analysisStatus: analysisStatus != null ? analysisStatus : q.analysisStatus,
          documentRefs: documentRefs != null ? documentRefs : q.documentRefs,
        };
        // A status update that does not carry the flag keeps the last one known, so a
        // live patch can never turn a known "no document" back into "awaiting".
        if (typeof hasDocument === "boolean") next.hasDocument = hasDocument;
        // Progress belongs to a run in flight: a value is taken as given, and a status
        // that is no longer queued or running clears whatever was shown.
        if (analysisProgress !== undefined) {
          next.analysisProgress = analysisProgress;
        } else if (q.analysisProgress != null && analysisStatus != null && !IN_FLIGHT.includes(analysisStatus)) {
          next.analysisProgress = null;
        }
        return next;
      });
      Vue.set(state.comparisonCache, submissionId, { ...existing, quotes });
    },
  },

  actions: {
    async fetchComparison({ commit, state }, { orgId, submissionId, refresh = false, silent = false }) {
      if (!orgId || !submissionId) return null;
      if (!refresh && state.comparisonCache[submissionId]) return state.comparisonCache[submissionId];
      try {
        const suffix = refresh ? `?_=${Date.now()}` : "";
        const { data } = await axios.get(comparisonUrl(orgId, submissionId, suffix), {
          withoutLoading: true,
          withoutErrorHandler: silent,
        });
        if (!data?.success) {
          commit("setComparisonError", { submissionId, message: readableReason(data?.message, "") });
          return null;
        }
        commit("cacheComparison", { submissionId, payload: data });
        return data;
      } catch (e) {
        commit("setComparisonError", { submissionId, message: readableReason(e?.response?.data?.message, "") });
        return null;
      }
    },

    async fetchAdvisory({ commit, state }, { orgId, submissionId, refresh = false, silent = false }) {
      if (!orgId || !submissionId) return null;
      if (!refresh && state.advisoryCache[submissionId]) return state.advisoryCache[submissionId];
      try {
        const suffix = refresh ? `?_=${Date.now()}` : "";
        const { data } = await axios.get(comparisonUrl(orgId, submissionId, `/advisory${suffix}`), {
          withoutLoading: true,
          withoutErrorHandler: silent,
        });
        if (!data?.success) return null;
        commit("cacheAdvisory", { submissionId, payload: data });
        return data;
      } catch (e) {
        return null;
      }
    },

    /** Re-analyse some or all quotes. Previous results stay until a new run completes. */
    async requestReanalysis({ commit, dispatch }, { orgId, submissionId, quoteIds }) {
      if (!orgId || !submissionId) return { success: false, message: REANALYSIS_FAILED };
      try {
        const body = Array.isArray(quoteIds) ? { quoteIds } : {};
        // The matrix says what went wrong in its own plain sentence, so the global
        // error toast would only repeat it with the server's wording.
        const { data } = await axios.post(comparisonUrl(orgId, submissionId, "/refresh"), body, {
          withoutLoading: true,
          withoutErrorHandler: true,
        });
        if (!data?.success) return { success: false, message: REANALYSIS_FAILED };
        const enqueued = Array.isArray(data.enqueued) ? data.enqueued : [];
        enqueued.forEach((quoteId) => {
          commit("patchComparisonQuoteStatus", { submissionId, quoteId, analysisStatus: "queued" });
        });
        dispatch("fetchComparison", { orgId, submissionId, refresh: true, silent: true });
        return { success: true, enqueued };
      } catch (e) {
        return { success: false, message: reanalysisFailureMessage(e) };
      }
    },

    /**
     * Attach a quote PDF. Refresh once; live status comes from `coverageAnalysisUpdated`
     * (Pusher) when the run starts or finishes.
     */
    async uploadSubmissionQuoteDocument(
      { dispatch, commit },
      { orgId, submissionId, quoteId, file, onProgress, documentType = QUOTE_DOCUMENT_TYPE }
    ) {
      if (!orgId || !submissionId) {
        return { success: false, message: "No submission selected." };
      }
      const scanReport = documentType === SCAN_REPORT_DOCUMENT_TYPE;
      const trimmedQuoteId = String(quoteId || "").trim();
      if (!trimmedQuoteId) {
        return { success: false, message: scanReport ? SCAN_REPORT_NO_CARRIER_ERROR : QUOTE_DOCUMENT_NO_QUOTE_ERROR };
      }
      if (!file) {
        return { success: false, message: "No file provided." };
      }
      if (!isPdfFile(file)) {
        return { success: false, message: PDF_ONLY_ERROR };
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", scanReport ? SCAN_REPORT_DOCUMENT_TYPE : QUOTE_DOCUMENT_TYPE);
      formData.append("quoteId", trimmedQuoteId);

      let data;
      try {
        const response = await axios.post(
          `/brokers/customers/${orgId}/submissions/${submissionId}${QUOTE_DOCUMENT_UPLOAD_PATH}`,
          formData,
          {
            withoutLoading: true,
            // A scan report's failures are told by the dialog in its own fixed sentence.
            ...(scanReport ? { withoutErrorHandler: true } : {}),
            timeout: uploadTimeoutMs(file),
            onUploadProgress: (e) => {
              if (!onProgress) return;
              const total = e && e.lengthComputable && e.total > 0 ? e.total : 0;
              if (!total) return;
              onProgress(Math.max(0, Math.min(Math.round((e.loaded / total) * 100), 100)));
            },
            transformRequest: [
              (body, headers) => {
                delete headers["Content-Type"];
                delete headers["content-type"];
                return body;
              },
            ],
          }
        );
        data = response?.data;
      } catch (e) {
        if (scanReport) {
          // A 404 says the scan summary is off: take the scan type away from the dialog.
          if (e?.response?.status === 404) {
            commit("scanSummary/setEnabled", { submissionId, enabled: false }, { root: true });
          }
          return { success: false, message: scanUploadFailureMessage(e) };
        }
        return { success: false, message: serverUploadError(e?.response?.data) };
      }

      if (!data?.success) {
        return { success: false, message: scanReport ? scanUploadFailureMessage(null, data) : serverUploadError(data) };
      }

      if (scanReport) {
        // The API marked the summary out of date at upload: show "Updating…" at once,
        // then read what it says. Pusher fills it in when the report has been read.
        commit("scanSummary/markUpdating", { submissionId }, { root: true });
        await dispatch("scanSummary/fetchSummary", { orgId, submissionId, refresh: true }, { root: true });
        return {
          success: true,
          // false when the report is saved but the hand-over to the reader is still queued.
          synced: data.synced !== false,
          analysisEnqueued: data.analysisEnqueued,
          document: data.document || null,
        };
      }

      await dispatch("fetchComparison", { orgId, submissionId, refresh: true, silent: true });
      await dispatch(SUBMISSION_DETAIL_ACTION, { orgId, submissionId, refresh: true }, { root: true });

      return {
        success: true,
        analysisEnqueued: data.analysisEnqueued,
        document: data.document || null,
      };
    },

    async saveComparisonOverride({ dispatch, commit }, { orgId, submissionId, quoteId, rowKey, valueText, note }) {
      if (!orgId || !submissionId || !quoteId || !rowKey) {
        return { success: false, message: "Could not save the override." };
      }
      try {
        const { data } = await axios.put(
          comparisonUrl(orgId, submissionId, "/overrides"),
          { quoteId, rowKey, valueText, note: note || null },
          { withoutErrorHandler: true }
        );
        if (!data?.success) return { success: false, message: OVERRIDE_SAVE_FAILED };
        commit("dropAdvisory", { submissionId });
        await dispatch("fetchComparison", { orgId, submissionId, refresh: true });
        return { success: true };
      } catch (e) {
        return { success: false, message: overrideFailureMessage(e) };
      }
    },

    async clearComparisonOverride({ dispatch, commit }, { orgId, submissionId, quoteId, rowKey }) {
      if (!orgId || !submissionId || !quoteId || !rowKey) {
        return { success: false, message: "Could not clear the override." };
      }
      try {
        const { data } = await axios.delete(comparisonUrl(orgId, submissionId, "/overrides"), {
          data: { quoteId, rowKey },
          withoutErrorHandler: true,
        });
        if (!data?.success) return { success: false, message: OVERRIDE_SAVE_FAILED };
        commit("dropAdvisory", { submissionId });
        await dispatch("fetchComparison", { orgId, submissionId, refresh: true });
        return { success: true };
      } catch (e) {
        return { success: false, message: overrideFailureMessage(e) };
      }
    },

    /**
     * A fresh link to the exact document a citation names, or null when it may not or
     * cannot be opened. Where the API resolves it (BWB) that includes a document the quote
     * no longer lists; otherwise it is found among the quote's own documents.
     */
    async fetchCitationDocument({ dispatch, rootState }, { orgId, submissionId, quoteId, documentKey }) {
      if (!orgId || !submissionId || !quoteId || !documentKey) return null;
      if (resolveCitationLocally) {
        try {
          return await resolveCitationLocally(dispatch, { orgId, submissionId, quoteId, documentKey }, { rootState });
        } catch (e) {
          return null;
        }
      }
      try {
        const params = new URLSearchParams({ quoteId, documentKey });
        const { data } = await axios.get(comparisonUrl(orgId, submissionId, `/documents?${params.toString()}`), {
          withoutLoading: true,
          withoutErrorHandler: true,
        });
        return (data?.success && data.document) || null;
      } catch (e) {
        return null;
      }
    },

    /**
     * One cell's source PDF with its excerpt highlighted on the cited page, as
     * a PDF Blob, or null. The server reads the excerpt from the stored cell. Any failure
     * is null and silent: the screen opens the plain document instead.
     */
    async fetchCitationPdf(_context, { orgId, submissionId, quoteId, rowKey } = {}) {
      if (!orgId || !submissionId || !quoteId || !rowKey) return null;
      try {
        const params = new URLSearchParams({ quoteId, rowKey });
        const response = await axios.get(comparisonUrl(orgId, submissionId, `/citation-pdf?${params.toString()}`), {
          responseType: "blob",
          withoutLoading: true,
          withoutErrorHandler: true,
          timeout: CITATION_PDF_TIMEOUT_MS,
        });
        const blob = response && response.data;
        if (typeof Blob === "undefined" || !(blob instanceof Blob) || !blob.size) return null;
        const header = (response.headers && response.headers["content-type"]) || "";
        const type = String(blob.type || header)
          .split(";")[0]
          .trim()
          .toLowerCase();
        if (type !== PDF_TYPE) return null;
        return blob.type ? blob : new Blob([blob], { type: PDF_TYPE });
      } catch (e) {
        return null;
      }
    },

    /**
     * Pusher `coverageAnalysisUpdated`: a mid-run report only moves the percentage, so it is
     * patched into the cached column; anything else re-reads what this tab has open.
     */
    onPusherCoverageAnalysisUpdated({ state, dispatch, commit }, payload) {
      const submissionId = payload && (payload.submissionId || payload.submission_id);
      if (!submissionId) return;
      const comparison = state.comparisonCache[submissionId];
      const advisory = state.advisoryCache[submissionId];
      const quoteId = payload.quoteId || payload.quote_id;
      // A progress report belongs to a run this column already shows as in flight. One that
      // arrives after the column has finished (a late event, or a run started elsewhere)
      // would move it back to "analysing" on its own word, so that case re-reads instead.
      const cachedQuote = (comparison && comparison.quotes ? comparison.quotes : []).find(
        (q) => (q.quoteId || q.quote_id) === quoteId
      );
      const cachedStatus = cachedQuote ? String(cachedQuote.analysisStatus || "").toLowerCase() : "";
      const finished = cachedStatus === "complete" || cachedStatus === "failed";
      if (IN_FLIGHT.includes(payload.status) && payload.analysisProgress && quoteId && comparison && !finished) {
        commit("patchComparisonQuoteStatus", {
          submissionId,
          quoteId,
          analysisStatus: payload.status,
          analysisProgress: payload.analysisProgress,
        });
        return;
      }
      const orgId =
        payload.organizationId ||
        payload.organization_id ||
        (comparison && (comparison.organizationId || comparison.organization_id)) ||
        (advisory && (advisory.organizationId || advisory.organization_id)) ||
        null;
      if (!orgId) return;
      if (comparison) dispatch("fetchComparison", { orgId, submissionId, refresh: true, silent: true });
      if (advisory) dispatch("fetchAdvisory", { orgId, submissionId, refresh: true, silent: true });
    },

    /** Pusher `comparisonUpdated`: patch cells and column status in place. */
    onPusherComparisonUpdated({ commit }, payload) {
      const submissionId = payload && (payload.submissionId || payload.submission_id);
      if (!submissionId) return;
      if (Array.isArray(payload.cells) && payload.cells.length) {
        commit("patchComparisonCells", { submissionId, cells: payload.cells });
      }
      for (const q of Array.isArray(payload.quotes) ? payload.quotes : []) {
        const quoteId = q.quoteId || q.quote_id;
        if (!quoteId) continue;
        commit("patchComparisonQuoteStatus", {
          submissionId,
          quoteId,
          analysisStatus: q.analysisStatus || q.analysis_status,
          documentRefs: q.documentRefs || q.document_refs,
          hasDocument: q.hasDocument !== undefined ? q.hasDocument : q.has_document,
        });
      }
    },
  },

  getters: {
    comparisonFor: (state) => (submissionId) => state.comparisonCache[submissionId] || null,
    advisoryFor: (state) => (submissionId) => state.advisoryCache[submissionId] || null,
    comparisonErrorFor: (state) => (submissionId) => state.comparisonErrors[submissionId] || "",
  },
};
